// src/services/api.js
import axios from "axios";

const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    // In production (Amplify/Cloud), if not specified, default to same-origin
    if (window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
        return window.location.origin;
    }
    return "http://localhost:5000";
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        "Content-Type": "application/json",
    },
});

// Module-level token getter. App.js injects this on mount via setAuthTokenGetter.
// We register the interceptor at module load (not inside a useEffect) so that
// requests fired by child components (e.g. AppDataContext) — whose effects run
// before parent effects in React — still pick up the Authorization header.
let tokenGetter = null;

// In-memory token cache. Auth0's getAccessTokenSilently() does its own caching
// internally, but the await still pays for promise resolution + JWT decode on
// every single request — measurable overhead when the dashboard fires 4
// parallel requests on load. We cache the JWT here and decode its `exp` claim
// once, then short-circuit the await until ~30s before expiry.
let cachedToken = null;
let cachedTokenExp = 0; // unix seconds

const decodeExp = (jwt) => {
    try {
        const payload = JSON.parse(atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
        return Number(payload.exp) || 0;
    } catch {
        return 0;
    }
};

export const setAuthTokenGetter = (fn) => {
    tokenGetter = fn;
    // Wipe cache on getter swap (e.g. logout/relogin)
    cachedToken = null;
    cachedTokenExp = 0;
};

export const isPublicApiPath = (url = "") => {
    const path = String(url).split("?", 1)[0];
    return path === "/api/configs" || path.startsWith("/api/v1/public/");
};

api.interceptors.request.use(async (config) => {
    // Public guest endpoints never need (or want) a bearer token. Skipping the
    // tokenGetter here avoids a doomed getAccessTokenSilently() round-trip per
    // request for logged-out visitors (the guest funnel + analytics live here).
    if (isPublicApiPath(config.url)) {
        return config;
    }
    if (tokenGetter) {
        try {
            const nowSec = Math.floor(Date.now() / 1000);
            // Use cache if token is valid for at least 30 more seconds
            if (cachedToken && cachedTokenExp - nowSec > 30) {
                config.headers.Authorization = `Bearer ${cachedToken}`;
            } else {
                const token = await tokenGetter();
                if (token) {
                    cachedToken = token;
                    cachedTokenExp = decodeExp(token);
                    config.headers.Authorization = `Bearer ${token}`;
                } else if (process.env.NODE_ENV !== "production") {
                    // Dev-only: helps pinpoint misconfigured Auth0
                    // audience during local setup. Never log the
                    // audience value in prod — it's system config.
                    console.error("[AUTH] tokenGetter returned empty token");
                }
            }
        } catch (e) {
            if (process.env.NODE_ENV !== "production") {
                console.error("[AUTH] tokenGetter failed:", e?.error || e?.message || e);
            }
        }
    }
    return config;
});

export const getQuestions = async (payload) => {
    const { data } = await api.post("/api/questions", payload);
    return data;
};

export const initUser = async (payload) => {
    // Handshake: Get or Create user in the active environment
    const { data } = await api.post("/api/users", payload);
    return data;
};

export const getAvailableQuestionsCount = async (payload) => {
    const { data } = await api.post("/api/questions/available", payload);
    return data.count;
};

export const startSession = async (payload) => {
    const { data } = await api.post("/api/sessions/start", payload);
    return data;
};

// ─── Labs ───────────────────────────────────────────────────────────────────
// Keep the wider timeout scoped to Labs so the rest of the application keeps
// its existing network behaviour.
const LABS_REQUEST_TIMEOUT_MS = 60000;
const labRequestConfig = (config = {}) => ({
    ...config,
    timeout: LABS_REQUEST_TIMEOUT_MS,
});

export const getLabs = async (certCode) => {
    const { data } = await api.get("/api/labs", labRequestConfig({
        params: certCode ? { cert_code: certCode } : undefined,
    }));
    return data;
};

export const getLab = async (labId) => {
    const requestId = window.crypto?.randomUUID?.()
        || `lab-open-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { data } = await api.get(
        `/api/labs/by-id/${encodeURIComponent(labId)}`,
        labRequestConfig({ headers: { "X-Learning-Request-Id": requestId } })
    );
    return data;
};

export const getCleanLabPreview = async (labId) => {
    const requestId = window.crypto?.randomUUID?.()
        || `lab-preview-open-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const { data } = await api.get(
        `/api/labs/qa-preview/by-id/${encodeURIComponent(labId)}`,
        labRequestConfig({ headers: { "X-Learning-Request-Id": requestId } })
    );
    return data;
};

const labMutationHeaders = (idempotencyKey) => ({
    "Idempotency-Key": idempotencyKey
        || window.crypto?.randomUUID?.()
        || `lab-${Date.now()}-${Math.random().toString(36).slice(2)}`,
});

export const startLabAttempt = async (certCode, labId, definitionVersion) => {
    const { data } = await api.post(
        `/api/labs/${encodeURIComponent(certCode)}/${encodeURIComponent(labId)}/attempts`,
        { definitionVersion },
        labRequestConfig({ headers: labMutationHeaders() })
    );
    return data;
};

export const startCleanLabPreviewAttempt = async (certCode, labId, definitionVersion) => {
    const { data } = await api.post(
        `/api/labs/qa-preview/${encodeURIComponent(certCode)}/${encodeURIComponent(labId)}/attempts`,
        { definitionVersion },
        labRequestConfig({ headers: labMutationHeaders() })
    );
    return data;
};

const labAttemptBase = (certCode, qaPreview = false) =>
    `/api/labs/${qaPreview ? "qa-preview/" : ""}${encodeURIComponent(certCode)}/attempts`;

export const saveLabAttemptState = async (certCode, attemptId, revision, workspaceState, qaPreview = false) => {
    const { data } = await api.put(
        `${labAttemptBase(certCode, qaPreview)}/${encodeURIComponent(attemptId)}/state`,
        { revision, workspaceState },
        labRequestConfig({ headers: labMutationHeaders() })
    );
    return data;
};

export const applyLabAttemptAction = async (
    certCode,
    attemptId,
    revision,
    action,
    idempotencyKey,
    qaPreview = false
) => {
    const { data } = await api.post(
        `${labAttemptBase(certCode, qaPreview)}/${encodeURIComponent(attemptId)}/actions`,
        { revision, action },
        labRequestConfig({ headers: labMutationHeaders(idempotencyKey) })
    );
    return data;
};

export const requestLabTestHint = async (
    certCode,
    attemptId,
    revision,
    testId,
    idempotencyKey,
    qaPreview = false
) => {
    const { data } = await api.post(
        `${labAttemptBase(certCode, qaPreview)}/${encodeURIComponent(attemptId)}/hints`,
        { revision, testId },
        labRequestConfig({ headers: labMutationHeaders(idempotencyKey) })
    );
    return data;
};

export const validateLabAttempt = async (certCode, attemptId, revision, workspaceState, qaPreview = false) => {
    const body = { revision };
    if (workspaceState !== undefined) body.workspaceState = workspaceState;
    const { data } = await api.post(
        `${labAttemptBase(certCode, qaPreview)}/${encodeURIComponent(attemptId)}/validate`,
        body,
        labRequestConfig({ headers: labMutationHeaders() })
    );
    return data;
};

export const resetLabAttempt = async (certCode, attemptId, revision, qaPreview = false) => {
    const { data } = await api.post(
        `${labAttemptBase(certCode, qaPreview)}/${encodeURIComponent(attemptId)}/reset`,
        { revision },
        labRequestConfig({ headers: labMutationHeaders() })
    );
    return data;
};

export const submitLabAttempt = async (certCode, attemptId, revision, qaPreview = false) => {
    const { data } = await api.post(
        `${labAttemptBase(certCode, qaPreview)}/${encodeURIComponent(attemptId)}/submit`,
        { revision },
        labRequestConfig({ headers: labMutationHeaders() })
    );
    return data;
};

export const getLabSolutions = async (certCode, labId, limit = 50) => {
    const { data } = await api.get(
        `/api/labs/${encodeURIComponent(certCode)}/${encodeURIComponent(labId)}/solutions`,
        labRequestConfig({ params: { limit } })
    );
    return data;
};

export const getCleanLabPreviewSolutions = async (certCode, labId, limit = 50) => {
    const { data } = await api.get(
        `/api/labs/qa-preview/${encodeURIComponent(certCode)}/${encodeURIComponent(labId)}/solutions`,
        labRequestConfig({ params: { limit } })
    );
    return data;
};

// ─── Dev Castle (internal local-development surface) ───
export const getCastleState = async (refresh = false) => {
    const { data } = await api.get(`/api/dev/castle/state${refresh ? "?refresh=1" : ""}`);
    return data;
};

export const getCastlePulse = async () => {
    const { data } = await api.get("/api/dev/castle/pulse");
    return data;
};

export const runCastleDiagnostics = async () => {
    const { data } = await api.post("/api/dev/castle/diagnostics");
    return data;
};

export const getStudyPath = async (certCode) => {
    const { data } = await api.get(`/api/study-path/${encodeURIComponent(certCode)}/roadmap`);
    return data;
};

export const getStudyPathRecommendation = async (certCode) => {
    const { data } = await api.get(`/api/study-path/${encodeURIComponent(certCode)}/recommendation`);
    return data;
};

export const getLearningCoverage = async (certCode, limit = 25) => {
    const { data } = await api.get(`/api/learning/${encodeURIComponent(certCode)}/coverage`, {
        params: { limit },
    });
    return data;
};

export const getLearningConcept = async (certCode, concept) => {
    const { data } = await api.get(`/api/learning/${encodeURIComponent(certCode)}/concept`, {
        params: { name: concept },
    });
    return data;
};

export const getTopicLearningContext = async (certCode, params = {}) => {
    const { data } = await api.get(`/api/learning/${encodeURIComponent(certCode)}/topic-context`, {
        params,
    });
    return data;
};

export const getLearningModule = async (certCode, nodeId) => {
    const { data } = await api.get(`/api/study-path/${encodeURIComponent(certCode)}/node/${encodeURIComponent(nodeId)}/module`);
    return data;
};

export const startStudyPathSession = async (certCode, payload = {}) => {
    const { data } = await api.post(`/api/study-path/${encodeURIComponent(certCode)}/start-session`, payload);
    return data;
};

export const getRoadmapV2Home = async (certCode) => {
    const { data } = await api.get(`/api/v2/roadmap/${encodeURIComponent(certCode)}/home`);
    return data;
};

export const getRoadmapV2Domain = async (certCode, domainId) => {
    const { data } = await api.get(
        `/api/v2/roadmap/${encodeURIComponent(certCode)}/domains/${encodeURIComponent(domainId)}`
    );
    return data;
};

export const getRoadmapV2Unit = async (certCode, unitId) => {
    const { data } = await api.get(
        `/api/v2/roadmap/${encodeURIComponent(certCode)}/units/${encodeURIComponent(unitId)}`
    );
    return data;
};

export const saveRoadmapV2UnitProgress = async (certCode, unitId, payload = {}) => {
    const { data } = await api.post(
        `/api/v2/roadmap/${encodeURIComponent(certCode)}/units/${encodeURIComponent(unitId)}/progress`,
        payload
    );
    return data;
};

export const startRoadmapV2UnitCheck = async (certCode, unitId, payload = {}) => {
    const { data } = await api.post(
        `/api/v2/roadmap/${encodeURIComponent(certCode)}/units/${encodeURIComponent(unitId)}/start-check`,
        payload
    );
    return data;
};

export const getRoadmapV2Result = async (sessionId) => {
    const { data } = await api.get(`/api/v2/roadmap/results/${encodeURIComponent(sessionId)}`);
    return data;
};

export const getRoadmapV2Remediation = async (remediationId) => {
    const { data } = await api.get(`/api/v2/roadmap/remediation/${encodeURIComponent(remediationId)}`);
    return data;
};

// Guided-node lesson continuity (phase, topicIndex).
export const saveNodeProgress = async (certCode, nodeId, patch = {}) => {
    const { data } = await api.post(
        `/api/study-path/${encodeURIComponent(certCode)}/node/${encodeURIComponent(nodeId)}/progress`,
        patch
    );
    return data;
};

export const getSession = async (sessionId) => {
    const { data } = await api.get(`/api/sessions/${encodeURIComponent(sessionId)}`);
    return data;
};

export const updateSession = async (sessionId, payload) => {
    const { data } = await api.put(`/api/sessions/${encodeURIComponent(sessionId)}`, payload);
    return data;
};

export const clearSession = async (sessionId) => {
    const { data } = await api.post(`/api/sessions/${encodeURIComponent(sessionId)}/clear`);
    return data;
};

export const clearEndlessSession = async (sessionId) => {
    const { data } = await api.post(`/api/endless/${encodeURIComponent(sessionId)}/clear`);
    return data;
};

export const submitSession = async (sessionId) => {
    const { data } = await api.post(`/api/sessions/${encodeURIComponent(sessionId)}/submit`);
    return data;
};

// Adaptive Mode: start (or resume) a fixed-length session with a short
// ahead-of-time runway, then post each answer to grade it and advance it.
// A recommended session passes its SessionSpec so the server plans the
// allocation from the same policy that produced the recommendation instead
// of recomputing the learner's state a second time.
export const startEndless = async (cert_code, sessionSpec = null) => {
    const { data } = await api.post("/api/endless/start", {
        cert_code,
        ...(sessionSpec ? { sessionSpec } : {}),
    });
    return data;
};

export const answerEndless = async (payload) => {
    const { data } = await api.post("/api/endless/answer", payload);
    return data;
};

export const getMoreEndlessQuestions = async (payload) => {
    const { data } = await api.post("/api/endless/more", payload);
    return data;
};

export const getDashboard = async (userId, certCode) => {
    const params = certCode ? `?cert_code=${encodeURIComponent(certCode)}` : "";
    const { data } = await api.get(`/api/users/${encodeURIComponent(userId)}/dashboard${params}`);
    return data;
};

// Per-cert blueprint + history analysis (powers the Surgical Audit grid on
// Analytics). Renamed from getDiagnostics — this was never the onboarding
// "diagnostic assessment", it's post-session audit data.
export const getSurgicalAudit = async (certCode, userId = null) => {
    const params = userId ? `?userId=${encodeURIComponent(userId)}` : "";
    const { data } = await api.get(`/api/v1/user/audit/${encodeURIComponent(certCode)}${params}`);
    return data;
};

export const completeOnboarding = async (userId, data) => {
    const res = await api.post(`/api/users/${encodeURIComponent(userId)}/onboarding`, data);
    return res.data;
};

export const getSmartQuestions = async (payload) => {
    const { data } = await api.post('/api/questions/smart', payload);
    return data;
};

export const reportQuestion = async (questionId, payload) => {
    const { data } = await api.post(`/api/questions/${encodeURIComponent(questionId)}/report`, payload);
    return data;
};

export const getQuestionCounts = async () => {
    const { data } = await api.get('/api/questions/counts');
    return data; // { "CLF-C02": 812, "AZ-104": 1043, ... }
};

// The public certification catalogue is small, changes rarely, and every public
// surface needs the same copy of it. Without a shared snapshot each page that
// mounts CertContext or usePublicCatalog refetched it from zero and gated its
// entire render on that round trip, so clicking a certification from the home
// page showed a spinner for data already sitting in the tab.
//
// Only a successful response is ever stored, so a reader still gets a
// server-confirmed catalogue rather than static marketing metadata, and every
// caller revalidates in the background so a stale one self-corrects.
let configsSnapshot = null;
let configsInFlight = null;

// Falls back to the catalogue the build inlined into the page shell, so a
// visitor arriving cold on a public page still gets a full first frame.
const shellCatalogue = () => {
    try {
        const element = document.getElementById("seo-catalogue");
        return element ? JSON.parse(element.textContent) : null;
    } catch (_) {
        return null;
    }
};

export const peekConfigs = () => configsSnapshot || shellCatalogue();

export const getConfigs = async () => {
    // Concurrent callers share one request; a later caller starts a fresh one.
    if (!configsInFlight) {
        configsInFlight = api.get("/api/configs")
            .then(({ data }) => { configsSnapshot = data; return data; })
            .finally(() => { configsInFlight = null; });
    }
    return configsInFlight;
};

let publicLabsSnapshot = null;
let publicLabsInFlight = null;

export const peekPublicLabs = () => publicLabsSnapshot;

export const getPublicLabs = async ({ briefingFor } = {}) => {
    if (briefingFor) {
        const { data } = await api.get("/api/v1/public/labs", {
            params: { briefing_for: briefingFor },
        });
        return data;
    }
    if (!publicLabsInFlight) {
        publicLabsInFlight = api.get("/api/v1/public/labs")
            .then(({ data }) => { publicLabsSnapshot = data; return data; })
            .finally(() => { publicLabsInFlight = null; });
    }
    return publicLabsInFlight;
};


export default api;
